/**
 * Program Detail Page - Modern Clean Design
 * Beautiful, intuitive layout matching university detail page
 */

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Loader2, ArrowLeft, GraduationCap, MapPin, Clock, DollarSign,
  Briefcase, Globe, BookOpen, Award, Check, Heart, ExternalLink,
  Users, Target, TrendingUp, Home
} from 'lucide-react';
import { Button } from '@voilajsx/uikit/button';
import { programAPI } from '../api/program-api';
import { SEO, Header } from '../../../shared/components';
import type { Program } from '../types';

// Modern clean color palette with green theme
const colors = {
  primary: '#059669',      // Dark green (Emerald 600)
  primaryLight: '#34D399', // Light green (Emerald 400)
  secondary: '#64748B',    // Slate
  accent: '#F59E0B',       // Amber
  success: '#10B981',      // Emerald
  info: '#14B8A6',         // Teal
  purple: '#14B8A6',       // Teal (replacing purple)
  orange: '#F97316',       // Orange
  rose: '#F43F5E',         // Rose
  background: '#F8FAFC',   // Very light slate
  card: '#FFFFFF',         // White
  border: '#E2E8F0',       // Light slate
  text: '#0F172A',         // Dark slate
  textMuted: '#64748B',    // Muted slate
  textLight: '#94A3B8'     // Light slate
};

const ProgramDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [program, setProgram] = useState<Program | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState('details');

  // Refs for scroll sections
  const detailsRef = useRef<HTMLDivElement>(null);
  const requirementsRef = useRef<HTMLDivElement>(null);
  const careerRef = useRef<HTMLDivElement>(null);
  const universityRef = useRef<HTMLDivElement>(null);

  const backUrl = (location.state as any)?.from || '/explore/programs';

  const navItems = [
    { id: 'details', label: 'Details', icon: BookOpen, ref: detailsRef },
    { id: 'requirements', label: 'Requirements', icon: Award, ref: requirementsRef },
    { id: 'career', label: 'Career', icon: Briefcase, ref: careerRef },
    { id: 'university', label: 'University', icon: GraduationCap, ref: universityRef },
  ];

  useEffect(() => {
    const loadProgram = async () => {
      if (!id) return;

      setIsLoading(true);
      setError(null);

      try {
        // Pass ID or slug to API - it handles both
        const programData = await programAPI.getProgramById(id);
        setProgram(programData);
      } catch (err) {
        console.error('Failed to load program:', err);
        setError('Failed to load program details');
      } finally {
        setIsLoading(false);
      }
    };

    loadProgram();
  }, [id]);

  // Scroll spy to update active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => ({
        id: item.id,
        element: item.ref.current
      }));

      const scrollPosition = window.scrollY + 120;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.element && section.element.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [program]);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.background }}>
        <div className="flex items-center gap-3">
          <Loader2 className="h-7 w-7 animate-spin" style={{ color: colors.primary }} />
          <span style={{ color: colors.text, fontSize: '16px', fontWeight: '500' }}>Loading program details...</span>
        </div>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.background }}>
        <div className="text-center">
          <p className="mb-4" style={{ color: colors.rose, fontSize: '16px' }}>{error || 'Program not found'}</p>
          <Button onClick={() => navigate(backUrl)} variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Programs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${program.name} - ${program.university.name}`}
        description={`${program.degree_type} in ${program.field} at ${program.university.name}. ${program.duration_years} years program.`}
      />

      <Header />

      <div style={{ minHeight: '100vh', background: colors.background }}>
        {/* Modern Hero Banner */}
        <div style={{
          background: '#1E293B',
          position: 'relative',
          overflow: 'hidden',
          paddingBottom: '48px'
        }}>
          {/* Background Image with Overlay */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1400&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.2
          }}></div>

          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.9) 0%, rgba(4, 120, 87, 0.95) 100%)'
          }}></div>

          <div className="container mx-auto px-6 py-6 relative z-10">
            {/* Back Button */}
            <button
              onClick={() => navigate(backUrl)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '32px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'translateX(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>

            {/* Hero Content */}
            <div className="max-w-5xl">
              {/* Program Name */}
              <h1 style={{
                fontSize: '3.75rem',
                fontWeight: '800',
                color: 'white',
                marginBottom: '24px',
                lineHeight: '1',
                letterSpacing: '-0.02em'
              }}>
                {program.name}
              </h1>

              {/* University & Location & Badges */}
              <div className="flex flex-wrap items-center gap-3 mb-8">
                <div style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  padding: '12px 20px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  height: '48px'
                }}>
                  <GraduationCap className="h-5 w-5" style={{ color: 'white' }} />
                  <span style={{ fontWeight: '600', fontSize: '16px', color: 'white' }}>{program.university.name}</span>
                </div>

                <div style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  padding: '12px 20px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  height: '48px'
                }}>
                  <span style={{ fontSize: '20px', lineHeight: '1' }}>{program.university.country.flag_emoji}</span>
                  <MapPin className="h-5 w-5" style={{ color: 'white' }} />
                  <span style={{ fontWeight: '600', fontSize: '16px', color: 'white' }}>
                    {program.university.city && `${program.university.city}, `}
                    {program.university.country.name}
                  </span>
                </div>

                {program.degree_type && (
                  <div style={{
                    background: colors.accent,
                    padding: '12px 20px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                    height: '48px'
                  }}>
                    <Award className="h-5 w-5" style={{ color: 'white' }} />
                    <span style={{ fontWeight: '700', fontSize: '16px', color: 'white' }}>
                      {program.degree_type}
                    </span>
                  </div>
                )}
              </div>

              {/* Key Stats - Modern Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {program.duration_years && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(10px)',
                    padding: '20px',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '6px', fontWeight: '500' }}>
                      Duration
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: 'white', lineHeight: '1' }}>
                      {program.duration_years}{program.duration_years === 1 ? 'yr' : 'yrs'}
                    </div>
                  </div>
                )}

                {program.tuition_usd && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(10px)',
                    padding: '20px',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '6px', fontWeight: '500' }}>
                      Tuition/Year
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: 'white', lineHeight: '1' }}>
                      ${(program.tuition_usd / 1000).toFixed(0)}k
                    </div>
                  </div>
                )}

                {program.avg_starting_salary_usd && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(10px)',
                    padding: '20px',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '6px', fontWeight: '500' }}>
                      Avg Salary
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: 'white', lineHeight: '1' }}>
                      ${(program.avg_starting_salary_usd / 1000).toFixed(0)}k
                    </div>
                  </div>
                )}

                {program.employment_rate_percentage && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(10px)',
                    padding: '20px',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '6px', fontWeight: '500' }}>
                      Employment
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: 'white', lineHeight: '1' }}>
                      {program.employment_rate_percentage}%
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons - Floating */}
            <div className="flex gap-3 mt-8">
              <button style={{
                background: 'white',
                color: colors.primary,
                padding: '16px 32px',
                borderRadius: '12px',
                fontWeight: '600',
                fontSize: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
              }}>
                <Heart className="h-5 w-5" />
                <span>Save Program</span>
              </button>

              <button
                onClick={() => navigate(`/university/${program.university.slug || program.university.id}`)}
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  padding: '16px 32px',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <ExternalLink className="h-5 w-5" />
                <span>View University</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content with Sidebar */}
        <div className="container mx-auto px-6 py-12">
          <div className="grid grid-cols-12 gap-10">
            {/* Modern Sticky Sidebar */}
            <div className="col-span-12 lg:col-span-3">
              <div style={{
                position: 'sticky',
                top: '100px'
              }}>
                <div style={{
                  background: colors.card,
                  borderRadius: '20px',
                  padding: '24px',
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                  border: `1px solid ${colors.border}`
                }}>
                  <h3 style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '20px'
                  }}>
                    Quick Navigation
                  </h3>

                  <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {navItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeSection === item.id;

                      return (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.ref)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 16px',
                            borderRadius: '12px',
                            border: 'none',
                            background: isActive ? colors.primary : 'transparent',
                            color: isActive ? 'white' : colors.textMuted,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            fontWeight: isActive ? '600' : '500',
                            fontSize: '14px',
                            textAlign: 'left',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = '#F1F5F9';
                              e.currentTarget.style.color = colors.text;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = colors.textMuted;
                            }
                          }}
                        >
                          {isActive && (
                            <div style={{
                              position: 'absolute',
                              left: 0,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: '3px',
                              height: '60%',
                              background: 'white',
                              borderRadius: '0 2px 2px 0'
                            }}></div>
                          )}
                          <Icon className="h-5 w-5" style={{ flexShrink: 0 }} />
                          <span style={{ flex: 1 }}>{item.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="col-span-12 lg:col-span-9">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>

                {/* Program Details Section */}
                <section ref={detailsRef} id="details">
                  <div style={{ marginBottom: '32px' }}>
                    <h2 style={{
                      fontSize: '32px',
                      fontWeight: '800',
                      color: colors.text,
                      marginBottom: '8px',
                      letterSpacing: '-0.01em'
                    }}>
                      Program Details
                    </h2>
                    <p style={{ fontSize: '16px', color: colors.textMuted }}>
                      Core information about this program
                    </p>
                  </div>

                  {/* Study Details Card */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '20px',
                    padding: '36px',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                    border: `1px solid ${colors.border}`,
                    marginBottom: '24px'
                  }}>
                    <div className="grid md:grid-cols-2 gap-10">
                      {program.field && (
                        <div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '700',
                            color: colors.textMuted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '12px'
                          }}>
                            Field of Study
                          </div>
                          <div style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            color: colors.text
                          }}>
                            {program.field}
                          </div>
                        </div>
                      )}
                      {program.sub_field && (
                        <div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '700',
                            color: colors.textMuted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '12px'
                          }}>
                            Specialization
                          </div>
                          <div style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            color: colors.text
                          }}>
                            {program.sub_field}
                          </div>
                        </div>
                      )}
                      {program.language_of_instruction && (
                        <div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '700',
                            color: colors.textMuted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '12px'
                          }}>
                            Language
                          </div>
                          <div style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            color: colors.text
                          }}>
                            {program.language_of_instruction}
                          </div>
                        </div>
                      )}
                      {program.total_credits && (
                        <div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '700',
                            color: colors.textMuted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '12px'
                          }}>
                            Total Credits
                          </div>
                          <div style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            color: colors.text
                          }}>
                            {program.total_credits}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Study Modes */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '32px', paddingTop: '32px', borderTop: `1px solid ${colors.border}` }}>
                      {program.is_full_time && (
                        <span style={{
                          padding: '12px 20px',
                          background: `${colors.success}15`,
                          color: colors.success,
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Check className="h-4 w-4" />
                          Full-time
                        </span>
                      )}
                      {program.is_part_time_available && (
                        <span style={{
                          padding: '12px 20px',
                          background: `${colors.info}15`,
                          color: colors.info,
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Check className="h-4 w-4" />
                          Part-time Available
                        </span>
                      )}
                      {program.is_online_available && (
                        <span style={{
                          padding: '12px 20px',
                          background: `${colors.primary}15`,
                          color: colors.primary,
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Globe className="h-4 w-4" />
                          Online Available
                        </span>
                      )}
                    </div>
                  </div>
                </section>

                {/* Requirements Section */}
                {(program.requirements_gpa_min || program.requirements_english_test) && (
                  <section ref={requirementsRef} id="requirements">
                    <div style={{ marginBottom: '32px' }}>
                      <h2 style={{
                        fontSize: '32px',
                        fontWeight: '800',
                        color: colors.text,
                        marginBottom: '8px',
                        letterSpacing: '-0.01em'
                      }}>
                        Admission Requirements
                      </h2>
                      <p style={{ fontSize: '16px', color: colors.textMuted }}>
                        Minimum requirements for admission
                      </p>
                    </div>

                    <div style={{
                      background: colors.card,
                      borderRadius: '20px',
                      padding: '36px',
                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                      border: `1px solid ${colors.border}`
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {program.requirements_gpa_min && (
                          <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '12px',
                              background: `${colors.success}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Check className="h-6 w-6" style={{ color: colors.success }} />
                            </div>
                            <div>
                              <div style={{
                                fontWeight: '700',
                                color: colors.text,
                                fontSize: '18px',
                                marginBottom: '6px'
                              }}>
                                Minimum GPA
                              </div>
                              <div style={{ fontSize: '16px', color: colors.textMuted }}>
                                {program.requirements_gpa_min} / 4.0
                              </div>
                            </div>
                          </div>
                        )}
                        {program.requirements_english_test && (
                          <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '12px',
                              background: `${colors.success}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Globe className="h-6 w-6" style={{ color: colors.success }} />
                            </div>
                            <div>
                              <div style={{
                                fontWeight: '700',
                                color: colors.text,
                                fontSize: '18px',
                                marginBottom: '6px'
                              }}>
                                English Proficiency
                              </div>
                              <div style={{ fontSize: '16px', color: colors.textMuted }}>
                                {program.requirements_english_test}
                                {program.requirements_english_score_min && ` - Minimum ${program.requirements_english_score_min}`}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                )}

                {/* Career Prospects Section */}
                {(program.career_prospects || program.top_employers) && (
                  <section ref={careerRef} id="career">
                    <div style={{ marginBottom: '32px' }}>
                      <h2 style={{
                        fontSize: '32px',
                        fontWeight: '800',
                        color: colors.text,
                        marginBottom: '8px',
                        letterSpacing: '-0.01em'
                      }}>
                        Career Prospects
                      </h2>
                      <p style={{ fontSize: '16px', color: colors.textMuted }}>
                        Career opportunities and top employers
                      </p>
                    </div>

                    <div style={{
                      background: colors.card,
                      borderRadius: '20px',
                      padding: '36px',
                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                      border: `1px solid ${colors.border}`
                    }}>
                      {program.career_prospects && (
                        <p style={{
                          fontSize: '16px',
                          color: colors.text,
                          lineHeight: '1.7',
                          marginBottom: program.top_employers ? '32px' : '0'
                        }}>
                          {program.career_prospects}
                        </p>
                      )}
                      {program.top_employers && program.top_employers.length > 0 && (
                        <div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '700',
                            color: colors.textMuted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '20px'
                          }}>
                            Top Employers
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                            {program.top_employers.map((employer, idx) => (
                              <span
                                key={idx}
                                style={{
                                  padding: '12px 20px',
                                  background: colors.background,
                                  color: colors.text,
                                  borderRadius: '12px',
                                  fontSize: '15px',
                                  fontWeight: '600',
                                  border: `1px solid ${colors.border}`
                                }}
                              >
                                {employer}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* University Info Section */}
                <section ref={universityRef} id="university">
                  <div style={{ marginBottom: '32px' }}>
                    <h2 style={{
                      fontSize: '32px',
                      fontWeight: '800',
                      color: colors.text,
                      marginBottom: '8px',
                      letterSpacing: '-0.01em'
                    }}>
                      About the University
                    </h2>
                    <p style={{ fontSize: '16px', color: colors.textMuted }}>
                      Learn more about {program.university.name}
                    </p>
                  </div>

                  <div style={{
                    background: colors.card,
                    borderRadius: '20px',
                    padding: '36px',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                    border: `1px solid ${colors.border}`
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '24px'
                    }}>
                      <div>
                        <h3 style={{
                          fontSize: '24px',
                          fontWeight: '700',
                          color: colors.text,
                          marginBottom: '8px'
                        }}>
                          {program.university.name}
                        </h3>
                        <p style={{ fontSize: '16px', color: colors.textMuted }}>
                          {program.university.country.flag_emoji} {program.university.city && `${program.university.city}, `}{program.university.country.name}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => navigate(`/university/${program.university.slug || program.university.id}`)}
                      style={{
                        width: '100%',
                        background: colors.primary,
                        color: 'white',
                        padding: '16px',
                        borderRadius: '12px',
                        border: 'none',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(5, 150, 105, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      View Full University Profile
                      <ExternalLink className="h-5 w-5" />
                    </button>
                  </div>
                </section>

              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProgramDetailPage;
